import { useEffect, useRef, useState } from "react"
import * as yup from "yup"
import qs from "qs"
import { useSearchParams } from "react-router-dom"
import { useStableNavigate } from "~/ui-rtk/utils/StableNavigateContext"
import { toast } from "react-toastify"

import { useCompleteMetaAccountUserOauthMutation } from "~/ui-rtk/api/metaAccountApi"
import { useCompleteMetaAgencyCredentialOauthMutation } from "~/ui-rtk/api/partnershipsApi"
import {
  CONNECT_CANCELLED,
  CONNECT_FAILED,
  META_ACCESS_MANAGE_URL,
  connectResultText,
} from "~/ui-rtk/components/features/settings/MetaAccessSettings/copy"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import { takeAgencyLoginMarker } from "~/ui-rtk/utils/agency-login-marker"
import { takeMetaAccountLoginMarker } from "~/ui-rtk/utils/meta-account-login-marker"
import {
  useCompanyConnectorControllerAuthenticateCompanyConnectorMutation,
  useCompanyConnectorControllerGetCompanyConnectorsQuery,
} from "../api/companyConnectorApi"
import { getServerErrorMessage, isNotFoundError } from "../utils/http-utils"
import { ConnectorTypeEnum } from "../components/features/connectors/ConnectorsTable/utils"
import { shouldAutoOpenService } from "../components/features/connectors/forms/utils"

const queryValidator = yup.object({
  authCode: yup.string().required(),
  state: yup.string().required(),
})

/**
 * Meta sends every login back to this page. A connector login carries
 * `{ companyConnectorId }` in `state`; the agency discovery credential's
 * login (AdBlaster → Partnerships → Manage → Connect with Facebook) carries
 * `{ agencyCredential: true, nonce }`, and an account user's login (Settings →
 * Company → Meta access → Manage account users) `{ metaAccountUser: true,
 * nonce }`, both minted and checked by the API. The page only routes the code
 * to the right endpoint — it never exchanges it.
 */
function isAgencyCredentialState(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as { agencyCredential?: unknown }).agencyCredential === true
  )
}

function isMetaAccountUserState(state: unknown): boolean {
  return (
    typeof state === "object" &&
    state !== null &&
    (state as { metaAccountUser?: unknown }).metaAccountUser === true
  )
}

/** The `state` Meta sent back, parsed; undefined when there is none or it is not JSON. */
function parseReturnedState(raw: string | null): unknown {
  if (!raw) return undefined
  try {
    return JSON.parse(raw)
  } catch {
    return undefined
  }
}

type AuthenticateConnectorProps = {
  redirectTo: string
}

const AuthenticateConnector: React.FC<AuthenticateConnectorProps> = ({
  redirectTo,
}) => {
  const navigate = useStableNavigate()
  const [searchParams] = useSearchParams()
  const [authenticate] =
    useCompanyConnectorControllerAuthenticateCompanyConnectorMutation()
  const [completeAgencyOauth] = useCompleteMetaAgencyCredentialOauthMutation()
  const [completeAccountUserOauth] = useCompleteMetaAccountUserOauthMutation()
  // The agency state's nonce is single use. React StrictMode replays the
  // mount effect in development, so latch before the first request rather
  // than spend the nonce twice and follow a success toast with an error.
  const agencyExchangeStarted = useRef(false)
  // The account-user state's nonce is single use too, for the same reason.
  const accountUserExchangeStarted = useRef(false)
  // A connector's code is single use too: without the latch the replay's
  // failed second exchange would follow the success with an error.
  const connectorExchangeStarted = useRef(false)
  // Whether this tab left for Meta from Manage → Connect with Facebook. Read
  // once: taking the marker spends it, and StrictMode replays the effect.
  const agencyLoginStarted = useRef<boolean | null>(null)
  // Whether this tab left for Meta from Meta access (Connect / Reconnect); read once, likewise.
  const accountUserLoginStarted = useRef<boolean | null>(null)

  const { data: companyConnectors } =
    useCompanyConnectorControllerGetCompanyConnectorsQuery()

  const [text, setText] = useState("Authenticating...")

  const redirect = (id?: string) => {
    if (id && companyConnectors) {
      // Find the company connector to check if it needs auto-open
      const companyConnector = companyConnectors.find(cc => cc.id === id)

      if (
        companyConnector &&
        shouldAutoOpenService(companyConnector.serviceName)
      ) {
        // For services that need setup, redirect with autoOpen parameter
        const redirectUrl = `${redirectTo}?autoOpen=${companyConnector.serviceName}`
        setTimeout(() => navigate(redirectUrl), 1000)
        return
      }
    }

    // Default redirect behavior for other connectors
    const redirectUrl = id
      ? `${redirectTo}?${qs.stringify({ state: { companyConnectorId: id, type: ConnectorTypeEnum.CUSTOM } })}`
      : redirectTo

    setTimeout(() => navigate(redirectUrl), 1000)
  }

  const showErrorMessage = (error: unknown) => {
    if (
      error instanceof yup.ValidationError ||
      (error instanceof Error && error.name === "ValidationError")
    ) {
      toast.error("Failed to authenticate the connector.", {
        toastId: "auth-connector-validation-error-id",
      })
    } else if (isNotFoundError(error)) {
      toast.error("Connection was not found.", {
        toastId: "auth-connector-not-found-error-id",
      })
    } else {
      toast.error(
        getServerErrorMessage(error, "Unexcepted error. Try again later."),
        { toastId: "auth-connector-server-error-id" },
      )
    }

    setText("Failed to authenticate connector. Redirecting...")
  }

  /**
   * Agency discovery credential: hand the code and the untouched state to the
   * API, then land on the Partnerships tab whatever the outcome — that is
   * where the login was started and where its status is shown.
   */
  const finishAgencyLogin = async (code: string, state: string) => {
    if (agencyExchangeStarted.current) return
    agencyExchangeStarted.current = true
    try {
      const saved = await completeAgencyOauth({ code, state }).unwrap()
      toast.success(
        saved.connectedUser
          ? `Agency discovery credential connected as ${saved.connectedUser.name}`
          : "Agency discovery credential connected.",
        { toastId: "agency-credential-oauth-success-id" },
      )
      setText("Success. Redirecting...")
    } catch (error: unknown) {
      // The API's message is the verdict (link expired or already used,
      // wrong app, a system user, missing scopes) — show it verbatim.
      toast.error(
        getServerErrorMessage(
          error,
          "Couldn't connect the agency discovery credential. Start again from Manage.",
        ),
        { toastId: "agency-credential-oauth-error-id" },
      )
      setText(
        "Failed to connect the agency discovery credential. Redirecting...",
      )
    }
    setTimeout(() => navigate(brandRoutes.AD_BLASTER.PARTNERSHIPS), 1000)
  }

  /**
   * Meta sends a cancelled login back without a code. For an agency login,
   * return to where it was started and say nothing changed, rather than
   * report a connector failure on the connectors page.
   */
  const endCancelledAgencyLogin = () => {
    toast.info(
      "The Facebook login did not finish. The agency discovery credential did not change.",
      { toastId: "agency-credential-oauth-cancelled-id" },
    )
    setText("Redirecting...")
    setTimeout(() => navigate(brandRoutes.AD_BLASTER.PARTNERSHIPS), 1000)
  }

  /**
   * Account user (Meta access): hand the code and the untouched state to the
   * API, then land on Meta access with Manage open whatever the outcome —
   * that is where the login was started and where its status is shown.
   */
  const finishAccountUserLogin = async (code: string, state: string) => {
    if (accountUserExchangeStarted.current) return
    accountUserExchangeStarted.current = true
    try {
      const result = await completeAccountUserOauth({ code, state }).unwrap()
      toast.success(connectResultText(result), {
        toastId: "meta-account-user-oauth-success-id",
      })
      setText("Success. Redirecting...")
    } catch (error: unknown) {
      // The API's message is the verdict (another person on Reconnect, link
      // expired or already used, missing scopes) — show it verbatim.
      toast.error(getServerErrorMessage(error, CONNECT_FAILED), {
        toastId: "meta-account-user-oauth-error-id",
      })
      setText("Failed to connect the account user. Redirecting...")
    }
    setTimeout(() => navigate(META_ACCESS_MANAGE_URL), 1000)
  }

  /** A cancelled account-user login: back to Meta access, nothing changed. */
  const endCancelledAccountUserLogin = () => {
    toast.info(CONNECT_CANCELLED, {
      toastId: "meta-account-user-oauth-cancelled-id",
    })
    setText("Redirecting...")
    setTimeout(() => navigate(META_ACCESS_MANAGE_URL), 1000)
  }

  useEffect(() => {
    const beginAction = async () => {
      agencyLoginStarted.current ??= takeAgencyLoginMarker()
      accountUserLoginStarted.current ??= takeMetaAccountLoginMarker()
      const hasCode = Boolean(
        searchParams.get("code") || searchParams.get("auth_code"),
      )
      if (!hasCode) {
        // The state Meta sent back names the login (a stale marker from an
        // abandoned agency or account-user login must not claim a
        // connector's cancel); the markers decide only when no state came back.
        const returnedState = parseReturnedState(searchParams.get("state"))
        const isAgencyLogin =
          returnedState === undefined
            ? agencyLoginStarted.current
            : isAgencyCredentialState(returnedState)
        if (isAgencyLogin) {
          endCancelledAgencyLogin()
          return
        }
        const isAccountUserLogin =
          returnedState === undefined
            ? accountUserLoginStarted.current
            : isMetaAccountUserState(returnedState)
        if (isAccountUserLogin) {
          endCancelledAccountUserLogin()
          return
        }
      }
      try {
        const result = await queryValidator.validate({
          state: searchParams.get("state"),
          authCode: searchParams.get("code") || searchParams.get("auth_code"),
        })

        const parsedState: unknown = JSON.parse(result.state)
        if (isAgencyCredentialState(parsedState)) {
          await finishAgencyLogin(result.authCode, result.state)
          return
        }
        if (isMetaAccountUserState(parsedState)) {
          await finishAccountUserLogin(result.authCode, result.state)
          return
        }

        const { companyConnectorId } = parsedState as {
          companyConnectorId: string
        }
        if (connectorExchangeStarted.current) return
        connectorExchangeStarted.current = true
        await authenticate({
          id: companyConnectorId,
          authenticateCompanyConnectorDto: {
            authCode: result.authCode,
          },
        }).unwrap()

        setText("Success. Redirecting...")
        void redirect(companyConnectorId)
      } catch (error: unknown) {
        void showErrorMessage(error)
        void redirect()
      }
    }

    void beginAction()
  }, [])

  return <div>{text}</div>
}

export default AuthenticateConnector
